import React, { useState } from "react";
import { EnvelopeSimple, Check, Pencil, X, ArrowSquareOut, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";
import { teleproApi } from "../lib/teleproApi";

/**
 * Settings card displayed at the top of the Telepro Portal.
 *
 * - If `pro_email` is empty: prompts the telepro to create a dedicated Gmail
 *   like "anthony-moncommerce68@gmail.com" (the regular moncommerce68@gmail.com
 *   is reserved for the platform owner).
 * - If set: shows the email with a Copy button + a small Edit pencil.
 */
/**
 * Build a Gmail-valid suggestion. Gmail's local part allows ONLY letters,
 * digits and dots (.) — no hyphens, no underscores, no other separators.
 * Format: `<prenom>.moncommerce68@gmail.com` (e.g. "damien.moncommerce68@gmail.com").
 */
function suggest(first_name) {
  const clean = (first_name || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
  return clean ? `${clean}.moncommerce68@gmail.com` : "prenom.moncommerce68@gmail.com";
}

export default function ProGmailCard({ me, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(me?.pro_email || suggest(me?.first_name));
  const [busy, setBusy] = useState(false);
  const hasEmail = Boolean(me?.pro_email);

  const save = async () => {
    const clean = (draft || "").trim().toLowerCase();
    if (clean) {
      if (!clean.includes("@")) {
        toast.error("Email invalide");
        return;
      }
      const [local, domain] = clean.split("@");
      if (domain !== "gmail.com" && domain !== "googlemail.com") {
        toast.error("Seules les adresses @gmail.com sont autorisées pour le compte pro");
        return;
      }
      if (!/^[a-z0-9.]+$/.test(local) || local.startsWith(".") || local.endsWith(".")) {
        toast.error("Gmail interdit les tirets/soulignés — utilise un point ou des chiffres");
        return;
      }
    }
    setBusy(true);
    try {
      const r = await teleproApi.patch("/me/profile", { pro_email: clean });
      onUpdated && onUpdated(r.data.telepro);
      setEditing(false);
      toast.success(clean ? "Gmail pro enregistré" : "Gmail pro retiré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(me.pro_email);
      toast.success("Copié dans le presse-papier");
    } catch {
      toast.error("Impossible de copier");
    }
  };

  if (hasEmail && !editing) {
    return (
      <div
        className="bg-card border border-border rounded-sm p-3 sm:p-4 mb-6 flex items-center gap-3 flex-wrap"
        data-testid="pro-gmail-card"
      >
        <div className="w-9 h-9 grid place-items-center rounded-sm bg-primary/10 text-primary shrink-0">
          <EnvelopeSimple size={16} weight="duotone"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Mon Gmail pro</div>
          <div className="font-mono text-sm font-medium truncate" data-testid="pro-gmail-value">{me.pro_email}</div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={copy}
            className="text-[11px] font-semibold text-foreground/80 hover:text-foreground bg-muted/50 hover:bg-muted border border-border rounded-sm px-2 py-1 inline-flex items-center gap-1"
            data-testid="pro-gmail-copy"
            title="Copier l'adresse"
          >
            <Copy size={11} weight="bold"/> Copier
          </button>
          <a
            href={`https://mail.google.com/mail/u/?authuser=${encodeURIComponent(me.pro_email)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-1 px-2 py-1"
            data-testid="pro-gmail-open"
            title="Ouvrir Gmail"
          >
            Ouvrir <ArrowSquareOut size={10} weight="bold"/>
          </a>
          <button
            onClick={() => { setDraft(me.pro_email); setEditing(true); }}
            className="text-muted-foreground hover:text-foreground p-1"
            data-testid="pro-gmail-edit"
            title="Modifier"
          >
            <Pencil size={12} weight="bold"/>
          </button>
        </div>
      </div>
    );
  }

  // Empty or editing mode
  return (
    <div
      className="bg-secondary/5 border border-secondary/30 rounded-sm p-4 mb-6"
      data-testid="pro-gmail-card-empty"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 grid place-items-center rounded-sm bg-secondary/15 text-secondary shrink-0">
          <EnvelopeSimple size={16} weight="duotone"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-medium text-sm mb-0.5">
            {hasEmail ? "Modifier mon Gmail pro" : "Configure ton Gmail pro"}
          </div>
          {!hasEmail && (
            <p className="text-xs text-muted-foreground leading-relaxed">
              Pour approcher les commerçants qui n&apos;ont qu&apos;un email comme contact, crée un Gmail dédié au format
              {" "}<code className="bg-card px-1 rounded font-mono">prenom.moncommerce68@gmail.com</code>.
              {" "}<b>Seul @gmail.com est accepté</b> — ça donne une image uniforme et sérieuse à toute l&apos;équipe. Gmail interdit les tirets et soulignés, utilise un point ou des chiffres pour séparer ton prénom.
              <br className="hidden sm:block"/>
              Le compte <code className="bg-card px-1 rounded">moncommerce68@gmail.com</code> est réservé au créateur.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 mt-3">
            <input
              type="email"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={suggest(me?.first_name)}
              className="w-full font-mono text-sm border border-border rounded-sm px-3 py-2 bg-background focus:border-primary outline-none"
              data-testid="pro-gmail-input"
              autoComplete="off"
            />
            <div className="flex gap-1.5">
              <button
                onClick={save}
                disabled={busy}
                className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-3 py-2 rounded-sm text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                data-testid="pro-gmail-save"
              >
                <Check size={12} weight="bold"/> Enregistrer
              </button>
              {hasEmail && (
                <button
                  onClick={() => { setEditing(false); setDraft(me.pro_email); }}
                  className="text-muted-foreground hover:text-foreground p-2"
                  data-testid="pro-gmail-cancel"
                >
                  <X size={14}/>
                </button>
              )}
            </div>
          </div>
          {!hasEmail && (
            <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
              👉 <a href="https://accounts.google.com/signup" target="_blank" rel="noopener noreferrer" className="underline text-primary font-semibold">Crée le compte Gmail sur Google</a>,
              puis reviens ici saisir l&apos;adresse exacte. Tu pourras la modifier à tout moment.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
